import React, { useEffect, useRef, useState, useCallback } from 'react';
import { MapPin, Navigation, ZoomIn, ZoomOut, RotateCcw, AlertCircle, Sparkles, ExternalLink } from 'lucide-react';
import { Companion } from '../../types';
import { getCoordinatesForCity } from '../../data/companionsData';
import { initGooglePlaces } from '../../lib/locationService';

export interface InteractiveGoogleMapProps {
  companions: Companion[];
  centerLocation?: { latitude?: number; longitude?: number; label?: string; formatted_address?: string } | null;
  selectedCompanionId?: string | null;
  onSelectCompanion: (companion: Companion) => void;
  onBookNow?: (companion: Companion, e: React.MouseEvent) => void;
  height?: string;
  className?: string;
  zoom?: number;
  userCoords?: { latitude: number; longitude: number } | null;
}

// Approximate bounding box for India coordinate normalization
const INDIA_BOUNDS = {
  minLat: 8.0,
  maxLat: 35.5,
  minLng: 68.0,
  maxLng: 97.5,
};

export const InteractiveGoogleMap: React.FC<InteractiveGoogleMapProps> = ({
  companions,
  centerLocation,
  selectedCompanionId,
  onSelectCompanion,
  onBookNow,
  height = '420px',
  className = '',
  zoom = 12,
  userCoords,
}) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);

  const [activeCompanion, setActiveCompanion] = useState<Companion | null>(null);
  const [useFallbackMap, setUseFallbackMap] = useState<boolean>(false);
  const [mapNotice, setMapNotice] = useState<string | null>(null);
  const [currentZoom, setCurrentZoom] = useState<number>(zoom);
  const [panOffset, setPanOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  // Effective center: centerLocation -> userCoords -> first companion -> default Kolkata (22.5726, 88.3639)
  const defaultCenter = { latitude: 22.5726, longitude: 88.3639, label: 'Kolkata, India' };
  const effectiveCenter = (centerLocation?.latitude && centerLocation?.longitude)
    ? { latitude: centerLocation.latitude, longitude: centerLocation.longitude, label: centerLocation.label || centerLocation.formatted_address || 'Selected Location' }
    : (userCoords ? { ...userCoords, label: 'My Location' } : defaultCenter);

  // Initialize Google Maps instance with graceful degradation
  useEffect(() => {
    let isCancelled = false;

    // Listen for Google Maps auth errors (e.g. ApiNotActivatedMapError / RefererNotAllowedMapError)
    const prevAuthFailure = (window as any).gm_authFailure;
    (window as any).gm_authFailure = () => {
      if (!isCancelled) {
        setUseFallbackMap(true);
        setMapNotice('Google Maps Platform API key requires Maps JavaScript API & Places API enablement in GCP Console. Active in High-Precision Vector Mode.');
      }
    };

    async function setupMap() {
      if (!mapContainerRef.current) return;

      try {
        const g = await initGooglePlaces();
        if (isCancelled) return;

        if (!g || !g.maps || !g.maps.Map) {
          setUseFallbackMap(true);
          return;
        }

        // Initialize Google Map instance
        const map = new g.maps.Map(mapContainerRef.current, {
          center: { lat: effectiveCenter.latitude, lng: effectiveCenter.longitude },
          zoom: currentZoom,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true,
          styles: [
            {
              featureType: 'poi',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }],
            },
          ],
        });

        mapInstanceRef.current = map;
        infoWindowRef.current = new g.maps.InfoWindow();

        // Clear existing markers
        markersRef.current.forEach((m) => m.setMap(null));
        markersRef.current = [];

        // Add markers for companions
        companions.forEach((comp) => {
          const coords = (comp.latitude && comp.longitude)
            ? { latitude: comp.latitude, longitude: comp.longitude }
            : getCoordinatesForCity(comp.city);

          const marker = new g.maps.Marker({
            position: { lat: coords.latitude, lng: coords.longitude },
            map,
            title: `${comp.name} - ₹${comp.hourlyRate}/hr`,
            icon: {
              path: g.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: '#ec4899',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2,
            },
          });

          marker.addListener('click', () => {
            setActiveCompanion(comp);
            onSelectCompanion(comp);

            if (infoWindowRef.current) {
              infoWindowRef.current.setContent(`
                <div style="font-family:system-ui,sans-serif;padding:6px;max-width:200px;">
                  <div style="font-weight:bold;font-size:13px;color:#0f172a;">${comp.name}, ${comp.age}</div>
                  <div style="font-size:11px;color:#64748b;margin-bottom:4px;">📍 ${comp.city}</div>
                  <div style="font-size:12px;font-weight:bold;color:#ec4899;">₹${comp.hourlyRate}/hr</div>
                </div>
              `);
              infoWindowRef.current.open(map, marker);
            }
          });

          markersRef.current.push(marker);
        });

        // Add user GPS marker if available
        if (userCoords) {
          new g.maps.Marker({
            position: { lat: userCoords.latitude, lng: userCoords.longitude },
            map,
            title: 'Your GPS Location',
            icon: {
              path: g.maps.SymbolPath.CIRCLE,
              scale: 9,
              fillColor: '#0284c7',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 3,
            },
          });
        }
      } catch (err) {
        if (!isCancelled) {
          setUseFallbackMap(true);
        }
      }
    }

    setupMap();

    // Responsive container resize observer: triggers map resize event when layout dimensions change
    const resizeObserver = new ResizeObserver(() => {
      if (mapInstanceRef.current && (window as any).google?.maps?.event) {
        (window as any).google.maps.event.trigger(mapInstanceRef.current, 'resize');
        if (effectiveCenter) {
          mapInstanceRef.current.setCenter({ lat: effectiveCenter.latitude, lng: effectiveCenter.longitude });
        }
      }
    });

    if (mapContainerRef.current) {
      resizeObserver.observe(mapContainerRef.current);
    }

    return () => {
      isCancelled = true;
      (window as any).gm_authFailure = prevAuthFailure;
      resizeObserver.disconnect();
      markersRef.current.forEach((m) => m.setMap(null));
      markersRef.current = [];
    };
  }, [companions, effectiveCenter.latitude, effectiveCenter.longitude, userCoords]);

  // Recenter Google Map when center changes
  useEffect(() => {
    if (mapInstanceRef.current && effectiveCenter) {
      mapInstanceRef.current.panTo({ lat: effectiveCenter.latitude, lng: effectiveCenter.longitude });
    }
  }, [effectiveCenter.latitude, effectiveCenter.longitude]);

  // Vector map coordinate projection helper (for high-precision fallback rendering)
  const projectCoords = useCallback((lat: number, lng: number) => {
    const latClamped = Math.max(INDIA_BOUNDS.minLat, Math.min(INDIA_BOUNDS.maxLat, lat));
    const lngClamped = Math.max(INDIA_BOUNDS.minLng, Math.min(INDIA_BOUNDS.maxLng, lng));

    // Mercator-like percent projection
    const x = ((lngClamped - INDIA_BOUNDS.minLng) / (INDIA_BOUNDS.maxLng - INDIA_BOUNDS.minLng)) * 100;
    const y = (1 - (latClamped - INDIA_BOUNDS.minLat) / (INDIA_BOUNDS.maxLat - INDIA_BOUNDS.minLat)) * 100;

    return { x: Math.max(4, Math.min(96, x)), y: Math.max(4, Math.min(96, y)) };
  }, []);

  return (
    <div
      className={`relative w-full rounded-3xl overflow-hidden border border-slate-200/90 shadow-sm bg-slate-900 ${className}`}
      style={{ height, minHeight: '320px', maxWidth: '100%' }}
    >
      {/* Live Google Maps Container (Active when Google Maps loads) */}
      <div
        ref={mapContainerRef}
        className={`w-full h-full ${useFallbackMap ? 'hidden' : 'block'}`}
        style={{ minHeight: '100%', width: '100%' }}
      />

      {/* High-Precision Interactive Vector Fallback Map (Active when client API key is restricted or offline) */}
      {useFallbackMap && (
        <div className="relative w-full h-full bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 flex flex-col justify-between overflow-hidden select-none">
          {/* Subtle geographical grid lines */}
          <div
            className="absolute inset-0 opacity-15 pointer-events-none"
            style={{
              backgroundImage: 'radial-gradient(circle at 1px 1px, #ec4899 1px, transparent 0)',
              backgroundSize: '24px 24px',
            }}
          />

          {/* India territory stylized outline watermark */}
          <svg
            className="absolute inset-0 w-full h-full opacity-10 pointer-events-none"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            <path
              d="M35 15 Q50 8 60 18 T75 35 Q85 55 70 75 T50 92 Q35 75 30 55 T35 15 Z"
              fill="#ec4899"
            />
          </svg>

          {/* Fallback Map Overlay Header */}
          <div className="relative z-10 p-3.5 flex items-center justify-between gap-2 flex-wrap bg-slate-950/60 backdrop-blur-md border-b border-slate-800/80">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-lg bg-pink-500/20 text-pink-400 flex items-center justify-center">
                <Navigation className="w-3.5 h-3.5" />
              </div>
              <div>
                <span className="text-xs font-black text-white tracking-tight block">
                  Interactive Companion Discovery Map (India)
                </span>
                <span className="text-[10px] text-slate-400">
                  Centered on {effectiveCenter.label || 'Kolkata, WB'} • {companions.length} Hosts Mapped
                </span>
              </div>
            </div>

            {/* Status notice */}
            <div className="flex items-center gap-1.5 text-[10px] bg-slate-800/90 text-slate-300 px-2.5 py-1 rounded-full border border-slate-700">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
              <span>Vector Coordinate Engine</span>
            </div>
          </div>

          {/* Interactive Plotting Canvas Area */}
          <div className="relative flex-1 w-full h-full">
            {/* Center Location Radar Pulse */}
            {effectiveCenter && (() => {
              const proj = projectCoords(effectiveCenter.latitude, effectiveCenter.longitude);
              return (
                <div
                  className="absolute pointer-events-none transform -translate-x-1/2 -translate-y-1/2"
                  style={{ left: `${proj.x}%`, top: `${proj.y}%` }}
                >
                  <div className="w-16 h-16 rounded-full bg-pink-500/20 animate-ping" />
                  <div className="absolute inset-0 m-auto w-3 h-3 rounded-full bg-pink-500 border-2 border-white shadow-lg" />
                </div>
              );
            })()}

            {/* Companions Location Pins */}
            {companions.map((comp) => {
              const coords = (comp.latitude && comp.longitude)
                ? { latitude: comp.latitude, longitude: comp.longitude }
                : getCoordinatesForCity(comp.city);
              const proj = projectCoords(coords.latitude, coords.longitude);
              const isSelected = selectedCompanionId === comp.id || activeCompanion?.id === comp.id;

              return (
                <div
                  key={comp.id}
                  className="absolute transform -translate-x-1/2 -translate-y-1/2 transition-all duration-300 z-20 cursor-pointer"
                  style={{ left: `${proj.x}%`, top: `${proj.y}%` }}
                  onClick={() => {
                    setActiveCompanion(comp);
                    onSelectCompanion(comp);
                  }}
                >
                  {/* Pin button */}
                  <div
                    className={`group flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-bold shadow-lg transition-transform ${
                      isSelected
                        ? 'bg-gradient-to-r from-pink-500 to-rose-600 text-white ring-4 ring-pink-500/30 scale-110 z-30'
                        : 'bg-slate-900/90 hover:bg-pink-600 text-white border border-slate-700 hover:scale-105'
                    }`}
                  >
                    <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse shrink-0" />
                    <span className="truncate max-w-[80px] sm:max-w-[100px]">{comp.name}</span>
                    <span className="font-mono text-[10px] text-pink-200">₹{comp.hourlyRate}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Map Controls & Status Footer */}
          <div className="relative z-10 p-3 flex items-center justify-between gap-3 bg-slate-950/70 backdrop-blur-md border-t border-slate-800">
            <span className="text-[10px] text-slate-400 flex items-center gap-1 truncate max-w-xs sm:max-w-md">
              <Sparkles className="w-3 h-3 text-pink-400 shrink-0" />
              <span>Click any marker to view host rates, bio & instant booking.</span>
            </span>

            <div className="flex items-center gap-1.5 shrink-0">
              <button
                type="button"
                onClick={() => setCurrentZoom((z) => Math.min(z + 1, 18))}
                className="w-7 h-7 rounded-lg bg-slate-800 hover:bg-slate-700 text-white flex items-center justify-center transition-colors cursor-pointer"
                title="Zoom In"
              >
                <ZoomIn className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setCurrentZoom((z) => Math.max(z - 1, 4))}
                className="w-7 h-7 rounded-lg bg-slate-800 hover:bg-slate-700 text-white flex items-center justify-center transition-colors cursor-pointer"
                title="Zoom Out"
              >
                <ZoomOut className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => {
                  setCurrentZoom(zoom);
                  setActiveCompanion(null);
                }}
                className="w-7 h-7 rounded-lg bg-slate-800 hover:bg-slate-700 text-white flex items-center justify-center transition-colors cursor-pointer"
                title="Reset View"
              >
                <RotateCcw className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Selected Companion Card Preview on Click */}
      {activeCompanion && (
        <div className="absolute bottom-4 left-4 right-4 sm:right-auto sm:w-80 z-30 animate-in fade-in slide-in-from-bottom-2 duration-200">
          <div className="bg-white rounded-2xl p-3.5 shadow-2xl border border-slate-200 flex items-center gap-3">
            <img
              src={activeCompanion.image}
              alt={activeCompanion.name}
              className="w-14 h-14 rounded-xl object-cover shrink-0 border border-slate-100"
              referrerPolicy="no-referrer"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-1">
                <h4 className="font-extrabold text-xs text-slate-900 truncate">
                  {activeCompanion.name}, {activeCompanion.age}
                </h4>
                <button
                  type="button"
                  onClick={() => setActiveCompanion(null)}
                  className="text-slate-400 hover:text-slate-600 text-xs p-1"
                >
                  ✕
                </button>
              </div>
              <p className="text-[11px] text-slate-500 truncate flex items-center gap-1 mt-0.5">
                <MapPin className="w-3 h-3 text-pink-500 shrink-0" />
                <span>{activeCompanion.city}</span>
              </p>
              <div className="flex items-center justify-between gap-2 mt-2 pt-1 border-t border-slate-100">
                <span className="text-xs font-black text-pink-600 font-mono">
                  ₹{activeCompanion.hourlyRate}/hr
                </span>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => onSelectCompanion(activeCompanion)}
                    className="px-2.5 py-1 text-[10px] font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors cursor-pointer"
                  >
                    View Details
                  </button>
                  {onBookNow && (
                    <button
                      type="button"
                      onClick={(e) => onBookNow(activeCompanion, e)}
                      className="px-3 py-1 text-[10px] font-extrabold text-white bg-gradient-to-r from-pink-500 to-rose-600 hover:from-pink-600 hover:to-rose-700 rounded-lg shadow-xs transition-transform active:scale-95 cursor-pointer"
                    >
                      Book Now ✨
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
